import { EMAIL_PATTERN } from 'constants/defaultConstants';
import { ChangeEvent, FC } from 'react';

import cleanText from 'utils/cleanText';
import normalizeEmail from 'utils/normalizeEmail';

import InputCharCounter from '../InputCharCounter';
import MandatoryField from '../MandatoryField';
import InputMessage, { MessageType } from '../InputMessage';
import IconTooltip from '../Icon/IconTooltip';
import Button from '../Buttons';

type InputTextProps = {
  id: string;
  value: string;
  label?: string;
  placeholder?: string;
  icon?: string;
  onChange: (value: string) => void; // eslint-disable-line
  onBlur?: VoidFunction; // eslint-disable-line
  required?: boolean;
  lgSize?: boolean;
  disabled?: boolean;
  emailInput?: boolean;
  marginBottom?: number;
  communicationInput?: boolean;
  onlyInput?: boolean;
  readOnly?: boolean;
  showCounter?: boolean;
  minLength?: number;
  maxLength?: number;
  additionalInfo?: string;
  additionalInfoType?: MessageType;
  tooltip?: string;
  isClearable?: boolean;
};

const DEFAULT_MAX_LENGTH = 50;

const InputText: FC<InputTextProps> = ({
  id,
  value,
  label,
  placeholder = '',
  onChange,
  onBlur,
  required = false,
  lgSize = true,
  disabled = false,
  icon,
  emailInput = false,
  marginBottom,
  communicationInput = false,
  onlyInput = false,
  readOnly = false,
  showCounter = false,
  minLength = 0,
  maxLength,
  additionalInfo,
  additionalInfoType,
  tooltip,
  isClearable = false,
}) => {
  const handleChangeInput = (event: ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;
    const sanitizedText = cleanText(value);

    if (emailInput) {
      const normalizedEmail = normalizeEmail(sanitizedText);
      onChange(normalizedEmail);
    } else {
      onChange(sanitizedText);
    }
  };

  const handleClearInput = () => {
    onChange('');
  };

  const inputClassName = communicationInput
    ? 'input-mail tagify'
    : `form-control ${lgSize ? 'form-control-lg' : ''}`;

  const InputComponent = (
    <input
      id={id}
      type={emailInput ? 'email' : 'text'}
      className={inputClassName}
      value={value}
      onChange={handleChangeInput}
      onBlur={onBlur}
      placeholder={placeholder}
      required={required}
      disabled={disabled}
      pattern={emailInput ? EMAIL_PATTERN : undefined}
      readOnly={readOnly}
      maxLength={maxLength}
      minLength={minLength}
    />
  );

  return (
    <>
      {communicationInput || onlyInput ? (
        InputComponent
      ) : (
        <div
          className={`form-group ${
            marginBottom !== undefined ? `mb-${marginBottom}` : ''
          }`}
        >
          {label && (
            <label className="form-label d-flex " htmlFor={id}>
              {label}{' '}
              {required && (
                <span className="ml-1">
                  <MandatoryField />
                </span>
              )}
              {tooltip && (
                <IconTooltip
                  id={`${id}-tooltip`}
                  tooltip={tooltip}
                  place="right"
                />
              )}
            </label>
          )}

          <div className="form-control-wrap">
            {icon && (
              <div className="form-icon form-icon-left">
                <em className={`icon ni ${icon}`} />
              </div>
            )}

            {InputComponent}

            {isClearable && value && !disabled && !readOnly && (
              <Button
                className="btn btn-sm bg-transparent border-0 shadow-none form-icon form-icon-right"
                onClick={handleClearInput}
              >
                <em className="icon ni ni-cross-sm" />
              </Button>
            )}

            {additionalInfo && (
              <InputMessage
                text={additionalInfo}
                type={additionalInfoType || MessageType.INFO}
              />
            )}

            {showCounter && (
              <InputCharCounter
                value={value}
                limit={maxLength || DEFAULT_MAX_LENGTH}
              />
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default InputText;
